import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { scheduleExceptionApiService, UnavailableDate } from '../../services/scheduleExceptionApi';

interface CalendarDay {
  date: number;
  isCurrentMonth: boolean;
  isToday?: boolean;
  isPast?: boolean;
  isUnavailable?: boolean;
  fullDate: string;
  unavailabilityInfo?: any;
}

interface CalendarViewProps {
  selectedDate: string;
  onDateSelect: (date: string) => void;
  unavailableDates: UnavailableDate[];
}

const CalendarView: React.FC<CalendarViewProps> = ({ 
  selectedDate, 
  onDateSelect,
  unavailableDates 
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const getDaysInMonth = (date: Date): CalendarDay[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days: CalendarDay[] = [];
    
    // Jours du mois précédent
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push({
        date: prevDate.getDate(),
        isCurrentMonth: false,
        fullDate: prevDate.toISOString().split('T')[0]
      });
    }
    
    // Jours du mois actuel
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      const today = new Date();
      const isToday = currentDate.toDateString() === today.toDateString();
      const isPast = currentDate < today && !isToday;
      const fullDate = currentDate.toISOString().split('T')[0];
      const isUnavailable = scheduleExceptionApiService.isDateUnavailable(fullDate, unavailableDates);
      
      days.push({
        date: day,
        isCurrentMonth: true,
        isToday,
        isPast,
        isUnavailable,
        fullDate,
        unavailabilityInfo: isUnavailable ? scheduleExceptionApiService.getUnavailabilityInfo(fullDate, unavailableDates) : null
      });
    }
    
    // Jours du mois suivant pour compléter la grille
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const nextDate = new Date(year, month + 1, day);
      days.push({
        date: day,
        isCurrentMonth: false,
        fullDate: nextDate.toISOString().split('T')[0]
      });
    }
    
    return days;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const handleDateSelect = (day: CalendarDay) => {
    if (day.isPast || day.isUnavailable) return;
    onDateSelect(day.fullDate);
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Choisir une date</h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h3 className="text-lg font-semibold text-gray-800 min-w-[200px] text-center">
            {currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
          </h3>
          <button
            onClick={() => navigateMonth('next')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>
      
      {/* Jours de la semaine */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map((day) => (
          <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>
      
      {/* Grille du calendrier */}
      <div className="grid grid-cols-7 gap-1">
        {getDaysInMonth(currentMonth).map((day, index) => (
          <button
            key={index}
            onClick={() => handleDateSelect(day)}
            disabled={day.isPast || day.isUnavailable}
            title={day.isUnavailable ? scheduleExceptionApiService.getUnavailabilityMessage(day.fullDate, unavailableDates) : ''}
            className={`
              aspect-square flex items-center justify-center text-sm rounded-lg transition-all duration-200 relative
              ${!day.isCurrentMonth ? 'text-gray-300' : ''}
              ${day.isPast ? 'text-gray-300 cursor-not-allowed' : ''}
              ${day.isUnavailable ? 'bg-red-100 text-red-400 cursor-not-allowed' : ''}
              ${!day.isPast && !day.isUnavailable ? 'hover:bg-blue-50' : ''}
              ${day.isToday && !day.isUnavailable ? 'bg-blue-100 text-blue-600 font-semibold' : ''}
              ${selectedDate === day.fullDate ? 'bg-blue-600 text-white font-semibold' : ''}
              ${day.isCurrentMonth && !day.isPast && !day.isToday && !day.isUnavailable && selectedDate !== day.fullDate ? 'text-gray-700' : ''}
            `}
          >
            {day.date}
            {day.isUnavailable && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border border-white"></div>
            )}
          </button>
        ))}
      </div>
      
      {/* Légende du calendrier */}
      <div className="mt-4 flex flex-wrap gap-4 text-xs">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-blue-100 rounded"></div>
          <span className="text-gray-600">Aujourd'hui</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-blue-600 rounded"></div>
          <span className="text-gray-600">Sélectionné</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-red-100 rounded relative">
            <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-red-500 rounded-full"></div>
          </div>
          <span className="text-gray-600">Indisponible</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-gray-200 rounded"></div>
          <span className="text-gray-600">Passé</span>
        </div>
      </div>
      
      {selectedDate && (
        <div className="mt-4 space-y-2">
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Date sélectionnée :</strong> {formatDate(selectedDate)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
